/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Extent } from 'ol/extent';
import ImageWrapper from 'ol/Image';
import ImageState from 'ol/ImageState';
import type { Projection } from 'ol/proj';
import { getRequestExtent } from 'ol/source/Image';
import type { ServerType } from 'ol/source/wms';
import { getImageSrc, getRequestParams } from 'ol/source/wms';
import type { WMImage } from '@opengeoweb/webmap';
import { WMImageStore } from '@opengeoweb/webmap';
import {
  ADD_SHOW_DIMS_OPTION,
  NUM_IMAGES_TO_CACHE,
  TIMEAWAREIMAGEWRAPPER_IMAGEDIDNOTLOADSUCCESFULLY,
  WMS_EXTENT_RATIO_TO_VIEWPORT,
} from './constants';

export const openLayersGetMapImageStore = new WMImageStore(
  NUM_IMAGES_TO_CACHE,
  {
    id: 'ol',
  },
);

export const clearImageCacheForAllMaps = (): void => {
  openLayersGetMapImageStore.clear();
};

interface TimeAwareGeoreferencedImageSrc {
  src: string;
  extent: Extent;
  resolution: number;
}
interface TimeAwareGeoreferencedImage {
  el: HTMLImageElement | HTMLCanvasElement;
  extent: Extent;
  resolution: number;
  isLoaded: boolean;
  hasError: boolean;
  isLoadedWithoutErrors: boolean;
  isLoading: boolean;
}

type TimeAwareGeoreferencedImageRequest = TimeAwareGeoreferencedImageSrc;

interface WorldSegment {
  requestExtent: Extent;
  displayExtent: Extent;
}

const splitExtentAtWorldSeams = (
  extent: Extent,
  worldExtent: Extent,
): WorldSegment[] => {
  const worldWidth = worldExtent[2] - worldExtent[0];
  if (worldWidth <= 0 || extent[2] <= extent[0]) {
    return [{ requestExtent: extent, displayExtent: extent }];
  }

  const segments: WorldSegment[] = [];
  let segmentStart = extent[0];
  let worldIndex = Math.floor((segmentStart - worldExtent[0]) / worldWidth);
  while (segmentStart < extent[2]) {
    let worldEnd = worldExtent[0] + (worldIndex + 1) * worldWidth;
    while (worldEnd <= segmentStart) {
      worldIndex += 1;
      worldEnd = worldExtent[0] + (worldIndex + 1) * worldWidth;
    }
    const segmentEnd = Math.min(extent[2], worldEnd);
    const worldOffset = worldIndex * worldWidth;
    segments.push({
      requestExtent: [
        segmentStart - worldOffset,
        extent[1],
        segmentEnd - worldOffset,
        extent[3],
      ],
      displayExtent: [segmentStart, extent[1], segmentEnd, extent[3]],
    });
    segmentStart = segmentEnd;
    worldIndex += 1;
  }
  return segments;
};

const combineExtents = (extents: Extent[]): Extent => [
  Math.min(...extents.map((extent) => extent[0])),
  Math.min(...extents.map((extent) => extent[1])),
  Math.max(...extents.map((extent) => extent[2])),
  Math.max(...extents.map((extent) => extent[3])),
];

const getExtentOverlapArea = (extentA: Extent, extentB: Extent): number => {
  const width = Math.max(
    0,
    Math.min(extentA[2], extentB[2]) - Math.max(extentA[0], extentB[0]),
  );
  const height = Math.max(
    0,
    Math.min(extentA[3], extentB[3]) - Math.max(extentA[1], extentB[1]),
  );
  return width * height;
};

interface RejectImageLoad {
  reason: string;
  url: string;
}

class TimeAwareImageWrapper extends ImageWrapper {
  private _extentToLoad: Extent;
  private _resolutionToLoad: number;
  private _pixelRatioToLoad: number;
  private _paramsToLoad: Record<string, string> = {};
  private _dimPropsToLoad: Record<string, string> = {};
  private _getMapUrl: string | null = null;
  private projection: Projection | null = null;
  private _resolvers: {
    resolve: (image: WMImage) => void;
    reject: (reason: RejectImageLoad) => void;
  }[] = [];
  private _styleName = 'default';
  private _layerName: string | null = null;
  private _extentRatio: number = WMS_EXTENT_RATIO_TO_VIEWPORT;
  private _compositeImageKey = '';
  private _compositeImage: HTMLCanvasElement | null = null;

  constructor(extent: Extent, resolution: number, pixelRatio: number) {
    super(extent, resolution, pixelRatio, ImageState.IDLE);
    this.extent = extent;
    this.resolution = resolution;
    this.state = ImageState.IDLE;
    this._extentToLoad = extent;
    this._pixelRatioToLoad = pixelRatio;
    this._resolutionToLoad = resolution;
    this._resolveAll = this._resolveAll.bind(this);
    this._rejectAll = this._rejectAll.bind(this);

    this.getWMSGetMapRequestURL = this.getWMSGetMapRequestURL.bind(this);
    this.hasImage = this.hasImage.bind(this);
    this.load = this.load.bind(this);
    this.loadAndResolve = this.loadAndResolve.bind(this);
    this.setGeoReferencedImage = this.setGeoReferencedImage.bind(this);
  }

  public setExtentRatio(extentRatio: number): void {
    this._extentRatio = extentRatio;
  }

  public getWMSGetMapRequestURL(
    extentToLoad: Extent,
    resolutionToLoad: number,
    __pixelRatio: number,
    projection: Projection | null,
    getMapUrl: string | null,
    params: Record<string, string>,
    layerName: string | null,
    styleName: string,
    dimProps: Record<string, string>,
  ): TimeAwareGeoreferencedImageSrc {
    const pixelRatio = 1; // TODO?
    if (
      !getMapUrl ||
      !layerName ||
      !extentToLoad ||
      extentToLoad?.length === 0
    ) {
      return { src: '', extent: this.extent, resolution: 1 };
    }
    const options = {
      hidpi: false,
      serverType: 'mapserver' as ServerType,
    };

    const resolution = resolutionToLoad;

    const requestResolution = Array.isArray(resolution)
      ? resolution[0]
      : resolution || 1;

    const hidpi = options.hidpi === undefined ? true : options.hidpi;
    const newPixelRatio =
      pixelRatio !== 1 && (!hidpi || options.serverType === undefined)
        ? 1
        : pixelRatio;

    // Load extentRatio^2 % larger WMS extents than the current view (default 1.2, see WMS_EXTENT_RATIO_TO_VIEWPORT)
    const ratio = this._extentRatio;
    const extent = extentToLoad;
    const newExtent = getRequestExtent(
      extent,
      requestResolution!,
      newPixelRatio,
      ratio,
    );
    // Feature in adaguc-server to display actually used dimensions in the map.
    const paramsToUse = { ...params };
    if (ADD_SHOW_DIMS_OPTION) {
      paramsToUse['SHOWDIMS'] = 'TRUE';
    }
    const src = getImageSrc(
      newExtent,
      resolution as unknown as number,
      pixelRatio,
      projection!,
      getMapUrl,
      getRequestParams(
        {
          ...paramsToUse,
          ...dimProps,
          LAYERS: layerName,
          STYLES: styleName,
        },
        'GetMap',
      ),
      options.serverType!,
    );
    return {
      src,
      extent: newExtent,
      resolution: resolution as unknown as number,
    };
  }

  public getAlternativeImage(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
    getMapUrl: string,
    params: Record<string, string>,
    layerName: string,
    styleName: string,
    dimsProps: Record<string, string>,
  ): TimeAwareGeoreferencedImage | false {
    const requests = this.getWMSGetMapRequestURLs(
      extent,
      resolution,
      pixelRatio,
      projection,
      getMapUrl,
      params,
      layerName,
      styleName,
      dimsProps,
    );

    const images = requests.map((request) => {
      const candidates = openLayersGetMapImageStore.getAltGeoReferencedImages(
        request.src,
        request.extent,
        request.resolution,
      );
      return candidates
        .filter((image) => image.isLoadedWithoutErrors() && image.geoReference)
        .map((image) => ({
          image,
          overlapArea: getExtentOverlapArea(
            image.geoReference!.extent as Extent,
            request.extent,
          ),
        }))
        .filter(({ overlapArea }) => overlapArea > 0)
        .sort((imageA, imageB) => imageB.overlapArea - imageA.overlapArea)[0]
        ?.image;
    });
    if (images.some((image) => !image)) {
      return false;
    }

    const completeImages = images as WMImage[];
    if (completeImages.length > 1) {
      const uniqueImages = [...new Set(completeImages)];
      if (uniqueImages.length !== completeImages.length) {
        if (uniqueImages.length !== 1) {
          return false;
        }
        const [image] = uniqueImages;
        const imageExtent = image.geoReference!.extent;
        const requestedExtent = combineExtents(
          requests.map((request) => request.extent),
        );
        if (
          imageExtent[0] > requestedExtent[0] ||
          imageExtent[1] > requestedExtent[1] ||
          imageExtent[2] < requestedExtent[2] ||
          imageExtent[3] < requestedExtent[3]
        ) {
          return false;
        }
        return {
          el: image.getElement(),
          extent: imageExtent,
          resolution: image.geoReference!.resolution,
          isLoaded: image.isLoaded(),
          isLoadedWithoutErrors: image.isLoadedWithoutErrors(),
          hasError: image.hasError(),
          isLoading: image.isLoading(),
        };
      }
      const imageRequests = completeImages.map((image, index) => ({
        ...requests[index],
        extent: image.geoReference!.extent,
        resolution: image.geoReference!.resolution,
      }));
      const image = this.createCompositeImage(completeImages, imageRequests);
      if (!image) {
        return false;
      }
      return {
        el: image,
        extent: combineExtents(imageRequests.map((request) => request.extent)),
        resolution: imageRequests[0].resolution,
        isLoaded: completeImages.every((loadedImage) => loadedImage.isLoaded()),
        isLoadedWithoutErrors: completeImages.every((loadedImage) =>
          loadedImage.isLoadedWithoutErrors(),
        ),
        hasError: completeImages.some((loadedImage) => loadedImage.hasError()),
        isLoading: completeImages.some((loadedImage) =>
          loadedImage.isLoading(),
        ),
      };
    }

    const [geoRef] = completeImages;
    return {
      el: geoRef.getElement(),
      extent: geoRef.geoReference!.extent,
      resolution: geoRef.geoReference!.resolution,
      isLoaded: geoRef.isLoaded(),
      isLoadedWithoutErrors: geoRef.isLoadedWithoutErrors(),
      hasError: geoRef.hasError(),
      isLoading: geoRef.isLoading(),
    };
  }

  public hasImage(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
    getMapUrl: string,
    params: Record<string, string>,
    layerName: string,
    styleName: string,
    dimsProps: Record<string, string>,
  ): TimeAwareGeoreferencedImage | false {
    const requests = this.getWMSGetMapRequestURLs(
      extent,
      resolution,
      pixelRatio,
      projection,
      getMapUrl,
      params,
      layerName,
      styleName,
      dimsProps,
    );

    const images = requests.map((request) =>
      openLayersGetMapImageStore.getImage(request.src),
    );
    if (images.some((image) => !image)) {
      return false;
    }

    if (images.length === 1) {
      const [imageEl] = images;
      return {
        el: imageEl.getElement(),
        extent: requests[0].extent,
        resolution: requests[0].resolution,
        isLoaded: imageEl.isLoaded(),
        isLoadedWithoutErrors: imageEl.isLoadedWithoutErrors(),
        hasError: imageEl.hasError(),
        isLoading: imageEl.isLoading(),
      };
    }

    const completeImages = images as WMImage[];
    if (!completeImages.every((image) => image.isLoaded())) {
      return false;
    }
    const image = this.createCompositeImage(completeImages, requests);
    if (!image) {
      return false;
    }
    return {
      el: image,
      extent: combineExtents(requests.map((request) => request.extent)),
      resolution: requests[0].resolution,
      isLoaded: true,
      isLoadedWithoutErrors: completeImages.every((loadedImage) =>
        loadedImage.isLoadedWithoutErrors(),
      ),
      hasError: completeImages.some((loadedImage) => loadedImage.hasError()),
      isLoading: completeImages.some((loadedImage) => loadedImage.isLoading()),
    };
  }

  private createCompositeImage(
    images: WMImage[],
    requests: TimeAwareGeoreferencedImageRequest[],
  ): HTMLCanvasElement | false {
    const extent = combineExtents(requests.map((request) => request.extent));
    const { resolution } = requests[0];
    const imageKey = JSON.stringify([
      images.map((image) => image.getElement().src),
      requests.map((request) => request.extent),
      resolution,
    ]);
    if (this._compositeImage && this._compositeImageKey === imageKey) {
      return this._compositeImage;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil((extent[2] - extent[0]) / resolution);
    canvas.height = Math.ceil((extent[3] - extent[1]) / resolution);
    const context = canvas.getContext('2d');
    if (!context) {
      return false;
    }

    images.forEach((image, index) => {
      if (image.hasError()) {
        return;
      }
      const imageExtent = requests[index].extent;
      const overlapsNextSegment = index < images.length - 1;
      const destinationWidth =
        (imageExtent[2] - imageExtent[0]) / resolution +
        (overlapsNextSegment ? 1 : 0);
      context.drawImage(
        image.getElement(),
        (imageExtent[0] - extent[0]) / resolution,
        (extent[3] - imageExtent[3]) / resolution,
        destinationWidth,
        (imageExtent[3] - imageExtent[1]) / resolution,
      );
    });

    this._compositeImageKey = imageKey;
    this._compositeImage = canvas;
    return canvas;
  }

  public setGeoReferencedImage(image: TimeAwareGeoreferencedImage): void {
    const { el, extent, resolution } = image;
    this.setImage(el);
    this.extent = extent;
    this.resolution = resolution;
  }

  public load(): void {
    const requests = this.getWMSGetMapRequestURLs(
      this._extentToLoad,
      this._resolutionToLoad,
      this._pixelRatioToLoad,
      this.projection,
      this._getMapUrl,
      this._paramsToLoad,
      this._layerName,
      this._styleName,
      this._dimPropsToLoad,
    );

    const images = requests.map((request) =>
      openLayersGetMapImageStore.getImageAndSetGeoReference(request.src, {
        extent: request.extent,
        resolution: request.resolution,
      }),
    );
    if (images.some((image) => !image)) {
      return;
    }

    if (images.length === 1) {
      const [imageEl] = images;
      if (!imageEl.isLoaded()) {
        this.state = ImageState.LOADING;
        this.changed();
        void imageEl.loadAwait().finally(() => this.load());
        return;
      }
      this.state = ImageState.LOADED;
      this.setImage(imageEl.getElement());
      this.extent = requests[0].extent;
      this.resolution = requests[0].resolution;
      this.changed();
      if (imageEl.hasError()) {
        this._rejectAll(imageEl.getSrc());
      } else {
        this._resolveAll(imageEl);
      }
      return;
    }

    const loadedImages = images as WMImage[];
    if (loadedImages.every((image) => image.isLoaded())) {
      this.state = ImageState.LOADED;
      const image = this.createCompositeImage(loadedImages, requests);
      if (!image) {
        this.state = ImageState.ERROR;
        this.changed();
        this._rejectAll(loadedImages[0].getSrc());
        return;
      }
      this.setImage(image);
      this.extent = combineExtents(requests.map((request) => request.extent));
      this.resolution = requests[0].resolution;
      this.changed();
      const failedImage = loadedImages.find((loadedImage) =>
        loadedImage.hasError(),
      );
      if (failedImage) {
        this._rejectAll(failedImage.getSrc());
      } else {
        this._resolveAll(loadedImages[0]);
      }
      return;
    }

    this.state = ImageState.LOADING;
    this.changed();
    void Promise.all(
      loadedImages.map(async (image) => image.loadAwait()),
    ).finally(() => this.load());
  }

  public setState(state: number): void {
    this.state = state;
  }

  private _rejectAll(url: string): void {
    while (this._resolvers.length !== 0) {
      const resolve = this._resolvers.pop();
      resolve?.reject({
        reason: TIMEAWAREIMAGEWRAPPER_IMAGEDIDNOTLOADSUCCESFULLY,
        url,
      });
    }
  }

  private _resolveAll(image: WMImage): void {
    while (this._resolvers.length !== 0) {
      const resolve = this._resolvers.pop();
      resolve?.resolve(image);
    }
  }

  public async loadAndResolve(
    extentToLoad: Extent,
    resolutionToLoad: number,
    pixelRatio: number,
    projection: Projection,
    getMapUrl: string,
    params: Record<string, string>,
    layerName: string,
    styleName: string,
    dimProps: Record<string, string>,
  ): Promise<WMImage> {
    this._extentToLoad = [...(extentToLoad || [])];
    this._resolutionToLoad = resolutionToLoad;
    this._paramsToLoad = { ...params };
    this._getMapUrl = getMapUrl;
    this._layerName = layerName;
    this._pixelRatioToLoad = pixelRatio;
    this.projection = projection;
    this._styleName = styleName;
    this._dimPropsToLoad = { ...dimProps };
    return new Promise((resolve, reject) => {
      this._resolvers.push({ resolve, reject });
      this.load();
    });
  }

  public getWMSGetMapRequestURLs(
    extentToLoad: Extent,
    resolutionToLoad: number,
    pixelRatio: number,
    projection: Projection | null,
    getMapUrl: string | null,
    params: Record<string, string>,
    layerName: string | null,
    styleName: string,
    dimProps: Record<string, string>,
  ): TimeAwareGeoreferencedImageRequest[] {
    const request = this.getWMSGetMapRequestURL(
      extentToLoad,
      resolutionToLoad,
      pixelRatio,
      projection,
      getMapUrl,
      params,
      layerName,
      styleName,
      dimProps,
    );
    const worldExtent = projection?.canWrapX() ? projection.getExtent() : null;
    if (!request.src || !worldExtent) {
      return [request];
    }

    const segments = splitExtentAtWorldSeams(request.extent, worldExtent);
    if (
      segments.length === 1 &&
      segments[0].requestExtent.every(
        (value, index) => value === request.extent[index],
      )
    ) {
      return [request];
    }

    const paramsToUse = { ...params };
    if (ADD_SHOW_DIMS_OPTION) {
      paramsToUse['SHOWDIMS'] = 'TRUE';
    }
    return segments.map(({ requestExtent, displayExtent }) => ({
      src: getImageSrc(
        requestExtent,
        request.resolution,
        1,
        projection!,
        getMapUrl!,
        getRequestParams(
          {
            ...paramsToUse,
            ...dimProps,
            LAYERS: layerName!,
            STYLES: styleName,
          },
          'GetMap',
        ),
        'mapserver',
      ),
      extent: displayExtent,
      resolution: request.resolution,
    }));
  }
}

export default TimeAwareImageWrapper;
